import { useEffect, useRef, useState } from 'react';
import type { Asset, IssuedKitItem, Station } from '@hcc/types';
import { usePersonStationKit } from '../../hooks/useKitOrders';
import type { KitDraftSeed } from '../../hooks/useKitDrafts';

/**
 * Owns the drawer's editable item list: seeds it once from a saved draft if
 * one exists and from the person's resolved kit otherwise, then provides row
 * edit / add / remove plus tracked-asset add/remove that keeps the per-asset
 * category shadow map (IssuedKitItem has no category field) in sync.
 */
export function useKitIssueItems(personId: string, station: Station, draft: KitDraftSeed) {
  const [items, setItems] = useState<IssuedKitItem[]>([]);
  // Carry the category for each tracked asset so the row can pick the right
  // field labels (Serial / IMEI / Registration / Size).
  const [assetCategoryById, setAssetCategoryById] = useState<Record<string, string | null>>({});

  // Load the resolved kit for this station and seed the item rows once. Sizes
  // are pre-filled from the submission and gated items are already dropped by
  // the server, so the operator just confirms and signs.
  const stationKit = usePersonStationKit(personId, station);
  // The nonce this list was last seeded at. Held twice on purpose: the ref
  // guards the effect synchronously, while `seeded` is derived so it turns
  // false in the very render the nonce changes. A one-render lag there would
  // let the autosave baseline itself against pre-reload state and write it
  // straight back.
  const seededKit = useRef<number | null>(null);
  const [seededNonce, setSeededNonce] = useState<number | null>(null);
  const seeded = seededNonce === draft.nonce;

  useEffect(() => {
    // Nothing seeds until we know whether a draft exists, so the operator
    // never sees template rows flash and then be replaced by saved work.
    if (seededKit.current === draft.nonce || !draft.ready) return;
    // A draft is a snapshot of what a desk had on screen, including any
    // ad-hoc rows they added, so it replaces the template outright.
    if (draft.items) {
      setItems(draft.items);
      setAssetCategoryById(draft.assetCategoryById ?? {});
      seededKit.current = draft.nonce;
      setSeededNonce(draft.nonce);
      return;
    }
    // Only seed from a fetch made after this drawer mounted: the cached
    // list can predate an issue whose broadcast never arrived, and
    // seeding from it would re-populate already-issued items.
    if (!stationKit.isFetchedAfterMount || !stationKit.data) return;
    setItems(
      stationKit.data.map((it) => ({
        name: it.name,
        quantity: it.quantity,
        size: it.size_required ? (it.size ?? '') : undefined,
      })),
    );
    seededKit.current = draft.nonce;
    setSeededNonce(draft.nonce);
  }, [stationKit.data, stationKit.isFetchedAfterMount, draft.ready, draft.items, draft.nonce]);

  function setItem(idx: number, patch: Partial<IssuedKitItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((prev) => [...prev, { name: '', quantity: 1 }]);
  }
  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  /**
   * Add an asset row to the items[] state. Pulls description as the
   * item name and copies any metadata.serial through so the existing
   * report columns still populate.
   */
  function addAssetRow(asset: Asset) {
    setItems((prev) => {
      if (prev.some((it) => it.asset_id === asset.id)) return prev;
      const serial = (asset.metadata?.serial as string | undefined) ?? undefined;
      const registration = (asset.metadata?.registration as string | undefined) ?? undefined;
      return [
        ...prev,
        {
          name: asset.description,
          quantity: 1,
          ...(serial ? { serial } : {}),
          ...(registration ? { registration } : {}),
          asset_id: asset.id,
        },
      ];
    });
    setAssetCategoryById((m) => ({ ...m, [asset.id]: asset.category }));
  }
  function removeAssetRow(assetId: string) {
    setItems((prev) => prev.filter((it) => it.asset_id !== assetId));
    setAssetCategoryById((m) => {
      const { [assetId]: _, ...rest } = m;
      return rest;
    });
  }

  const selectedAssetIds = items.map((it) => it.asset_id).filter((id): id is string => Boolean(id));

  return {
    items,
    seeded,
    assetCategoryById,
    selectedAssetIds,
    setItem,
    addItem,
    removeItem,
    addAssetRow,
    removeAssetRow,
  };
}
